import { MagnifyingGlassIcon, PhotoIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import { memo, useCallback, useMemo, useState } from 'react';
import { CheckBox } from '../../../components/inputs/CheckBox';
import { Input } from '../../../components/inputs/Input';
import { T, useT } from '../../../translation/src';
import { ProjectAccessLevel, useProjectsQuery } from '../../../types/graphqlTypes';

/** Which projects a guest may open, and how: absent = no access. */
export type ProjectAccessSelection = ReadonlyMap<string, ProjectAccessLevel>;

export const ACCESS_LEVELS: ProjectAccessLevel[] = [ProjectAccessLevel.View, ProjectAccessLevel.Comment, ProjectAccessLevel.Edit];

/** The level a project starts with when access is first granted: what guests always had. */
export const DEFAULT_ACCESS_LEVEL = ProjectAccessLevel.Comment;

/** Select value for "no access"; the levels themselves are the enum values. */
const NONE = 'NONE';
/** Select value of the bulk control's placeholder entry. */
const PLACEHOLDER = '';

export const useAccessLevelTranslations = (): Record<ProjectAccessLevel, string> => ({
  [ProjectAccessLevel.View]: useT('view', { swc: true }),
  [ProjectAccessLevel.Comment]: useT('comment', { swc: true }),
  [ProjectAccessLevel.Edit]: useT('edit', { swc: true }),
});

interface AccessLevelSelectProps {
  /** undefined = no access */
  value: ProjectAccessLevel | undefined;
  onChange: (level: ProjectAccessLevel | undefined) => void;
  /** Text of the extra first entry that means "pick one"; it never becomes the value (used by the bulk control). */
  placeholder?: string;
  /** Offer "None" (no access). Off where no access makes no sense, e.g. the level for a new invitation. */
  allowNone?: boolean;
  disabled?: boolean;
  className?: string;
}

/** Native select over None + the three levels; small enough for a table row. */
const AccessLevelSelect_: React.FC<AccessLevelSelectProps> = ({ value, onChange, placeholder, allowNone = true, disabled, className }) => {
  const labels = useAccessLevelTranslations();
  const none = useT('none', { swc: true });
  const ariaLabel = useT('access', { swc: true });
  const current = placeholder !== undefined ? PLACEHOLDER : (value ?? NONE);
  return (
    <select
      value={current}
      disabled={disabled}
      aria-label={ariaLabel}
      onChange={(event) => {
        if (event.target.value === PLACEHOLDER) return;
        onChange(event.target.value === NONE ? undefined : (event.target.value as ProjectAccessLevel));
      }}
      onClick={(event) => event.stopPropagation()}
      className={classNames(
        'px-2 py-1 text-sm border rounded-md cursor-pointer dark:bg-[#2c2c2c] dark:text-white disabled:opacity-50 disabled:cursor-not-allowed',
        // The popup list is drawn by the browser: it follows color-scheme, not the page's classes.
        'dark:[color-scheme:dark] [&>option]:bg-white [&>option]:text-black dark:[&>option]:bg-[#2c2c2c] dark:[&>option]:text-white',
        value === undefined && placeholder === undefined ? 'border-gray-300 text-gray-500 bg-white dark:border-gray-600 dark:text-gray-400' : 'border-neon-green-600 bg-neon-green-300/20 dark:border-neon-green-300 dark:bg-neon-green-300/10',
        className,
      )}
    >
      {placeholder !== undefined && (
        <option value={PLACEHOLDER} disabled>
          {placeholder}
        </option>
      )}
      {allowNone && <option value={NONE}>{none}</option>}
      {ACCESS_LEVELS.map((level) => (
        <option key={level} value={level}>
          {labels[level]}
        </option>
      ))}
    </select>
  );
};
export const AccessLevelSelect = memo(AccessLevelSelect_);

interface ProjectAccessPickerProps {
  organisationId: string;
  selection: ProjectAccessSelection;
  onChange: (next: ProjectAccessSelection) => void;
  /** Skip loading the project list while the host dialog is closed. */
  active?: boolean;
}

/**
 * Every project of the organisation as a table row: thumbnail, name, and one access select
 * (None / View / Comment / Edit). Rows can be ticked to set many at once. Shared by the
 * per-guest dialog and the add-member dialog; the picker owns search and row ticks only, the
 * access selection belongs to the host.
 */
const ProjectAccessPicker_: React.FC<ProjectAccessPickerProps> = ({ organisationId, selection, onChange, active = true }) => {
  const searchProjects = useT('search projects', { swc: true });
  const setAccessForSelected = useT('set access for selected…', { swc: true });
  const { data, loading } = useProjectsQuery({ variables: { organisationId }, skip: !active });
  const projects = useMemo(() => [...(data?.projectsByOrganisationId ?? [])].sort((a, b) => a.name.localeCompare(b.name)), [data?.projectsByOrganisationId]);
  const [search, setSearch] = useState('');
  const [onlyWithAccess, setOnlyWithAccess] = useState(false);
  /** Rows ticked for a bulk change; independent of access. */
  const [ticked, setTicked] = useState<ReadonlySet<string>>(new Set());

  const visibleProjects = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return projects.filter((project) => (!needle || project.name.toLowerCase().includes(needle)) && (!onlyWithAccess || selection.has(project.id)));
  }, [projects, search, onlyWithAccess, selection]);

  const setLevels = useCallback(
    (projectIds: Iterable<string>, level: ProjectAccessLevel | undefined) => {
      const next = new Map(selection);
      for (const projectId of projectIds) {
        if (level === undefined) next.delete(projectId);
        else next.set(projectId, level);
      }
      onChange(next);
    },
    [selection, onChange],
  );

  const toggleTick = useCallback((projectId: string) => {
    setTicked((current) => {
      const next = new Set(current);
      if (next.has(projectId)) next.delete(projectId);
      else next.add(projectId);
      return next;
    });
  }, []);
  const allVisibleTicked = visibleProjects.length > 0 && visibleProjects.every((project) => ticked.has(project.id));
  const toggleAllVisible = useCallback(() => {
    setTicked((current) => {
      const next = new Set(current);
      if (allVisibleTicked) visibleProjects.forEach((project) => next.delete(project.id));
      else visibleProjects.forEach((project) => next.add(project.id));
      return next;
    });
  }, [allVisibleTicked, visibleProjects]);
  const applyToTicked = useCallback(
    (level: ProjectAccessLevel | undefined) => {
      setLevels(ticked, level);
      setTicked(new Set());
    },
    [ticked, setLevels],
  );

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <div className="relative flex-grow min-w-48">
          <MagnifyingGlassIcon className="absolute w-4 h-4 text-gray-400 -translate-y-1/2 pointer-events-none left-3 top-1/2" />
          <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder={searchProjects} aria-label={searchProjects} className="w-full !pl-9" />
        </div>
        <label className="flex items-center gap-2 text-sm cursor-pointer select-none whitespace-nowrap">
          <CheckBox checked={onlyWithAccess} onChange={() => setOnlyWithAccess((value) => !value)} className="rounded" />
          <T _str="with access only" swc />
        </label>
        <span className="text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">
          <T _str="{selected} of {total} projects" args={{ selected: selection.size, total: projects.length }} />
        </span>
      </div>

      <div className="border border-gray-200 rounded-lg dark:border-gray-700">
        {/* Header: tick-all, column labels, and the bulk control. The control is always mounted
            (disabled until rows are ticked) so the header keeps one height and nothing jumps. */}
        <div className="grid grid-cols-[auto_4rem_1fr_9rem] items-center gap-3 px-3 py-1.5 text-xs font-bold tracking-wide text-gray-500 uppercase border-b border-gray-200 dark:border-gray-700 dark:text-gray-400">
          <CheckBox checked={allVisibleTicked} onChange={toggleAllVisible} disabled={!visibleProjects.length} className="rounded" />
          <span />
          <span className="flex items-center justify-between gap-3">
            <span>
              <T _str="project" swc />
            </span>
            <span className="flex items-center gap-3 normal-case">
              <span className={classNames('transition-opacity', ticked.size ? 'opacity-100' : 'opacity-0')}>
                <T _str="{count} selected" args={{ count: ticked.size }} />
              </span>
              <AccessLevelSelect value={undefined} placeholder={setAccessForSelected} onChange={applyToTicked} disabled={!ticked.size} />
            </span>
          </span>
          <span>
            <T _str="access" swc />
          </span>
        </div>

        <div className="overflow-x-hidden overflow-y-auto divide-y divide-gray-100 dark:divide-gray-800 max-h-[50vh] dark:[color-scheme:dark]">
          {visibleProjects.map((project) => {
            const level = selection.get(project.id);
            const isTicked = ticked.has(project.id);
            return (
              <div
                key={project.id}
                onClick={() => toggleTick(project.id)}
                className={classNames('grid grid-cols-[auto_4rem_1fr_9rem] items-center gap-3 px-3 py-1.5 cursor-pointer select-none', isTicked ? 'bg-french-violet-600/10 dark:bg-french-violet-400/10' : 'hover:bg-gray-50 dark:hover:bg-[#2c2c2c]')}
              >
                <CheckBox checked={isTicked} onChange={() => toggleTick(project.id)} onClick={(event) => event.stopPropagation()} className="rounded" />
                <div className="flex items-center justify-center w-16 h-10 overflow-hidden bg-gray-100 rounded dark:bg-[#404040]">
                  {project.previewUrl ? <img src={project.previewUrl} alt="" loading="lazy" className="object-cover w-full h-full" /> : <PhotoIcon className="w-5 h-5 text-gray-400" />}
                </div>
                <span className={classNames('min-w-0 text-sm truncate', level ? 'font-medium' : 'text-gray-600 dark:text-gray-300')} title={project.name}>
                  {project.name}
                </span>
                <AccessLevelSelect value={level} onChange={(next) => setLevels([project.id], next)} className="w-full" />
              </div>
            );
          })}
          {!loading && !visibleProjects.length && (
            <div className="py-8 text-sm text-center text-gray-500 dark:text-gray-400">{search || onlyWithAccess ? <T _str="No projects match." /> : <T _str="This organisation has no projects yet." />}</div>
          )}
        </div>
      </div>
    </div>
  );
};

export const ProjectAccessPicker = memo(ProjectAccessPicker_);
